import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

interface PaginationProps {
    currentPage: number;
    totalPages: number;
    onPageChange: (page: number) => void;
}

export const Pagination: React.FC<PaginationProps> = ({ currentPage, totalPages, onPageChange }) => {
    if (totalPages <= 1) return null;

    const pages = Array.from({ length: totalPages }, (_, i) => i + 1);

    // Simple ellipsis logic could be added here for many pages, 
    // but starting with a simple list for reasonable amounts.
    // For now, let's limit visible pages if there are too many.

    let visiblePages = pages;
    if (totalPages > 7) {
        if (currentPage <= 4) {
            visiblePages = [...pages.slice(0, 5), -1, totalPages]; // -1 as ellipsis
        } else if (currentPage >= totalPages - 3) {
            visiblePages = [1, -1, ...pages.slice(totalPages - 5)];
        } else {
            visiblePages = [1, -1, currentPage - 1, currentPage, currentPage + 1, -1, totalPages];
        }
    }

    return (
        <div className="flex items-center justify-center gap-2 mt-6">
            <button
                onClick={() => onPageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className={`p-2 rounded-xl transition-all ${currentPage === 1
                        ? 'text-gray-600 cursor-not-allowed'
                        : 'text-gray-400 hover:text-white hover:bg-white/5'
                    }`}
            >
                <ChevronLeft size={20} />
            </button>

            <div className="flex items-center gap-1.5 bg-black/20 p-1.5 rounded-xl border border-white/5">
                {visiblePages.map((page, index) => {
                    if (page === -1) {
                        return <span key={`ellipsis-${index}`} className="px-2 text-gray-600">...</span>;
                    }

                    const isCurrent = page === currentPage;
                    return (
                        <button
                            key={page}
                            onClick={() => onPageChange(page as number)}
                            className={`
                                w-8 h-8 rounded-lg text-sm font-medium transition-all
                                ${isCurrent
                                    ? 'bg-primary text-white shadow-[0_0_15px_-3px_rgba(129,140,248,0.4)]'
                                    : 'text-gray-400 hover:text-white hover:bg-white/10'
                                }
                            `}
                        >
                            {page}
                        </button>
                    );
                })}
            </div>

            <button
                onClick={() => onPageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className={`p-2 rounded-xl transition-all ${currentPage === totalPages
                        ? 'text-gray-600 cursor-not-allowed'
                        : 'text-gray-400 hover:text-white hover:bg-white/5'
                    }`}
            >
                <ChevronRight size={20} />
            </button>
        </div>
    );
};
